import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useState } from 'react';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';
import { useAuth } from './AuthProvider.js';
import { GoogleContinueButton } from './GoogleContinueButton.js';
import { RegisterLegalConsent } from './RegisterLegalConsent.js';
import { RegisterAsModelLink } from './RegisterAsModelLink.js';
const ADJECTIVES = [
    'velvet',
    'shadow',
    'neon',
    'crimson',
    'midnight',
    'electric',
    'satin',
    'onyx',
];
const NOUNS = ['fox', 'panther', 'kitten', 'raven', 'viper', 'wolf', 'sphinx', 'cobra'];
function suggestUsername() {
    const adjective = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)] ?? 'velvet';
    const noun = NOUNS[Math.floor(Math.random() * NOUNS.length)] ?? 'fox';
    const suffix = Math.floor(Math.random() * 90) + 10;
    return `${adjective}_${noun}${suffix}`;
}
export function AuthInlineForm({ initialMode = 'register', onSuccess, className, }) {
    const { login, register } = useAuth();
    const [mode, setMode] = useState(initialMode);
    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const isRegister = mode === 'register';
    const handleSuggestUsername = useCallback(() => {
        setUsername(suggestUsername());
    }, []);
    async function handleSubmit(event) {
        event.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            if (isRegister) {
                if (!acceptedTerms) {
                    setError(t('common.auth.termsRequired'));
                    return;
                }
                await register({ email, username, password });
            }
            else {
                await login({ email, password });
            }
            onSuccess();
        }
        catch {
            setError(isRegister ? t('common.auth.registerError') : t('common.auth.loginError'));
        }
        finally {
            setSubmitting(false);
        }
    }
    const rootClass = ['auth-inline', className].filter(Boolean).join(' ');
    return (_jsxs("div", { className: rootClass, children: [_jsx("h2", { className: "auth-inline__title", children: isRegister ? t('common.auth.registerTitle') : t('common.auth.loginTitle') }), _jsx("p", { className: "auth-inline__subtitle", children: isRegister ? t('common.auth.registerSubtitle') : t('common.auth.loginSubtitle') }), _jsxs("form", { className: "auth-inline__form", onSubmit: (event) => void handleSubmit(event), noValidate: true, children: [isRegister ? (_jsxs("label", { className: "auth-inline__field", children: [_jsx("span", { children: t('common.auth.username') }), _jsxs("div", { className: "auth-inline__username-row", children: [_jsx("input", { type: "text", autoComplete: "username", required: true, minLength: 3, maxLength: 30, pattern: "[a-zA-Z0-9_]+", value: username, onChange: (event) => {
                                            setUsername(event.target.value);
                                        }, className: "fm-input auth-inline__username-input" }), _jsx("button", { type: "button", className: "auth-inline__suggest", onClick: handleSuggestUsername, "aria-label": t('common.auth.suggestUsername'), title: t('common.auth.suggestUsername'), children: "\u2726" })] })] })) : null, _jsxs("label", { className: "auth-inline__field", children: [_jsx("span", { children: t('common.auth.email') }), _jsx("input", { type: "email", autoComplete: "email", required: true, value: email, onChange: (event) => {
                                    setEmail(event.target.value);
                                }, className: "fm-input" })] }), _jsxs("label", { className: "auth-inline__field", children: [_jsx("span", { children: t('common.auth.password') }), _jsx("input", { type: "password", autoComplete: isRegister ? 'new-password' : 'current-password', required: true, minLength: 8, value: password, onChange: (event) => {
                                    setPassword(event.target.value);
                                }, className: "fm-input" })] }), isRegister ? (_jsx(RegisterLegalConsent, { id: "auth-inline-age-consent", checked: acceptedTerms, disabled: submitting, onCheckedChange: setAcceptedTerms })) : null, error ? (_jsx("p", { role: "alert", className: "fm-error", children: error })) : null, _jsx(Button, { label: submitting
                            ? t('common.auth.loading')
                            : isRegister
                                ? t('common.auth.submitRegister')
                                : t('common.auth.submitLogin'), type: "submit", disabled: submitting || (isRegister && !acceptedTerms) })] }), _jsx("div", { className: "auth-inline__divider", "aria-hidden": true, children: _jsx("span", {}) }), _jsx(GoogleContinueButton, { disabled: submitting || (isRegister && !acceptedTerms), onSuccess: onSuccess, onError: (message) => {
                    setError(message);
                } }), _jsxs("p", { className: "auth-inline__switch", children: [isRegister ? t('common.auth.hasAccount') : t('common.auth.noAccount'), ' ', _jsx("button", { type: "button", className: "auth-inline__switch-btn", onClick: () => {
                            setMode(isRegister ? 'login' : 'register');
                            setError(null);
                        }, children: isRegister ? t('common.nav.signIn') : t('common.nav.signUp') })] }), isRegister ? _jsx(RegisterAsModelLink, { className: "auth-inline__model" }) : null] }));
}
//# sourceMappingURL=AuthInlineForm.js.map